import { DisposalSet, MaybeArray, select, selectAll } from '@neptune/cx/utils';
import { reinit as reinitCategoryTreeFacet } from '../../../widget-templates/ay-category-tree-facet';
import { reinit as reinitCategoryListFacet } from '../../../widget-templates/ay-category-list-facet';


const SPINNER_MIN_DISPLAY_DURATION = 400;

export type MultiParamMap = Map<string, Set<string>>;

export const toMultiParamMap = (params: URLSearchParams): MultiParamMap => {
    const result: MultiParamMap = new Map();
    for (const [key, value] of params.entries()) {
        addToMultiParamMap(result, key, value);
    }
    return result;
}

export const toURLSearchParams = (multiParamMap: MultiParamMap): URLSearchParams => {
    const params = new URLSearchParams();
    for (const [key, values] of multiParamMap.entries()) {
        for (const value of values) {
            params.append(key, value);
        }
    }
    return params;
}

export const addToMultiParamMap = (multiParamMap: MultiParamMap, key: string, value: MaybeArray<string>): MultiParamMap => {
    if (multiParamMap.has(key)) {
        const existingSet = multiParamMap.get(key)!;
        if (Array.isArray(value)) {
            for (const v of value) {
                existingSet.add(v);
            }
        } else {
            existingSet.add(value);
        }
    } else {
        multiParamMap.set(key, new Set(Array.isArray(value) ? value : [value]));
    }
    return multiParamMap;
};

export const valuesFromMultiParamMap = (multiParamMap: MultiParamMap, key: string): Set<string> => {
    const set = multiParamMap.get(key);
    return set ? set : new Set<string>();
}


export const replaceInMultiParamMap = (multiParamMap: MultiParamMap, key: string, value: MaybeArray<string>): MultiParamMap => {
    const nSet = new Set(Array.isArray(value) ? value : [value])
    if (nSet.size === 0) {
        multiParamMap.delete(key);
    } else {
        multiParamMap.set(key, nSet);
    }
    return multiParamMap;
}


export const removeFromMultiParamMap = (multiParamMap: MultiParamMap, key: string, value: MaybeArray<string> | null = null): MultiParamMap => {
    if (multiParamMap.has(key)) {
        if (value === null) {
            multiParamMap.delete(key);
        } else {
            const set = multiParamMap.get(key);
            if (set) {
                if (Array.isArray(value)) {
                    for (const v of value) {
                        set.delete(v);
                    }
                } else {
                    set.delete(value);
                }
                if (set.size === 0) {
                    multiParamMap.delete(key);
                }
            }
        }
    }
    return multiParamMap;
};


export const selectors = {
    spinner: '.ay-search-layout__spinner',
    countText: '.ay-search-layout__count-text',
    resultContainer: '.ay-search-layout__results',
    facetsContainer: '.ay-search-layout__facets-container',
    activeFacetsWidget: '.ay-search-layout__active-facets',
    activeFacetsList: '.ay-search-layout__active-facets__list',
    queryInput: 'input.search-bar-keywords-input',
    categoryListFacet: '.ay-category-list-facet',
    categoryTreeFacet: '.ay-category-tree-facet',
    activeFacetRemove: '.ay-search-layout__active-facets__list .ay-chip a',
    activeFacetsRemoveAll: '.ay-search-layout__active-facets__clear',
} as const;


const activeFacetGC = new DisposalSet();
export const reinitActiveFacets = (
    searchLayoutEl: HTMLDivElement,
    queryInputEl: HTMLInputElement,
    pageParam: string
) => {
    activeFacetGC.disposeAll();
    const clearEls = selectAll<HTMLAnchorElement>(searchLayoutEl, selectors.activeFacetRemove);
    const clearAllEl = select<HTMLAnchorElement>(searchLayoutEl, selectors.activeFacetsRemoveAll);

    for (const clearEl of clearEls) {
        (activeFacetGC.register(clearEl)
            .addListener('click', e => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                const facetKind = clearEl.dataset.facetKind;
                const facetParam = clearEl.dataset.facetParam;
                const facetValue = clearEl.dataset.facetValue;
                if (facetKind === 'query') {
                    if (queryInputEl) queryInputEl.value = '';
                }
                if (!facetParam || !facetValue) {
                    updateResults(searchLayoutEl, clearAllEl.href, pageParam);
                } else {
                    // this path is preferable because it would make it possible to remove a 2nd (or 3rd, 4th, etc)
                    // active facets before the request triggered by removing the 1st facet completed. Otherwise, the
                    // 1st facet would re-appear after the 2nd facet request completed
                    const multiParamMap = toMultiParamMap(new URLSearchParams(window.location.search));
                    removeFromMultiParamMap(multiParamMap, facetParam, (facetKind === 'query'
                        ? null
                        : facetValue
                    ));
                    removeFromMultiParamMap(multiParamMap, pageParam, null);

                    const params = toURLSearchParams(multiParamMap);
                    const nextUrl = `${window.location.pathname}?${params}`;
                    updateResults(searchLayoutEl, nextUrl, pageParam);
                }
            })
        );
    }

    (activeFacetGC.register(clearAllEl)
        .addListener('click', e => {
            e.preventDefault();
            e.stopPropagation();
            if (queryInputEl) queryInputEl.value = '';
            updateResults(searchLayoutEl, clearAllEl.href, pageParam);
        })
    );
}

export const forceHardPagination = (resultsEl: HTMLDivElement) => {
  const paginationLinks = selectAll<HTMLAnchorElement>(resultsEl, '.pagination-bar nav a');
  for (const link of paginationLinks) {
      link.dataset.sennaOff = 'true';
  }
}

const httpState = {
    requestsInFlight: 0,
    lastRequestStarted: 0,
    lastRequestComplete: 0
};

export const updateResults = async (searchLayoutEl: HTMLDivElement, nextUrl: string, pageParam: string) => {
    const start = Date.now();
    try {
        const resultsWrapperEl = select<HTMLDivElement>(searchLayoutEl, selectors.resultContainer);
        const countTextEl = select<HTMLDivElement>(searchLayoutEl, selectors.countText);
        const spinnerEl = select<HTMLDivElement>(searchLayoutEl, selectors.spinner);
        const activeFacetsWidgetEl = select<HTMLDivElement>(searchLayoutEl, selectors.activeFacetsWidget);
        const queryInputEl = select<HTMLInputElement>(searchLayoutEl, selectors.queryInput);

        httpState.requestsInFlight++;
        spinnerEl.style.display = 'block';
        window.history.pushState({
            redirectPath: window.location.pathname + window.location.search,
            path: nextUrl,
            referrer: window.location.href,
            senna: true,
            scrollLeft: 0,
            scrollTop: 0,
            form: false,
        }, '', nextUrl);

        const myReqOffset = ++httpState.lastRequestStarted;

        const resp = await fetch(nextUrl);
        const html = await resp.text();

        if (myReqOffset >= httpState.lastRequestComplete) {
            httpState.lastRequestComplete = myReqOffset;

            const parser = new DOMParser();
            const resultDoc = parser.parseFromString(html, 'text/html');
            const updateResultsEl = select<HTMLDivElement>(resultDoc, selectors.resultContainer);
            const updateCountTextEl = select<HTMLDivElement>(resultDoc, selectors.countText);
            const updateActiveFacetsWidgetEl = select<HTMLDivElement>(resultDoc, selectors.activeFacetsWidget);
            const updateActiveFacetsListEl = select<HTMLDivElement>(resultDoc, selectors.activeFacetsList);
            const updateFacetControlsEl = resultDoc.querySelector<HTMLDivElement>(selectors.facetsContainer);
            const updateCategoryListFacetEls = (updateFacetControlsEl
                ? selectAll<HTMLDivElement>(updateFacetControlsEl, selectors.categoryListFacet)
                : []
            );
            const updateCategoryTreeFacetEls = (updateFacetControlsEl
                ? selectAll<HTMLDivElement>(updateFacetControlsEl, selectors.categoryTreeFacet)
                : []
            );

            const doUpdate = () => {
                const facetControlsEl = searchLayoutEl.querySelector<HTMLDivElement>(selectors.facetsContainer);
                resultsWrapperEl.innerHTML = updateResultsEl.innerHTML;
                activeFacetsWidgetEl.innerHTML = updateActiveFacetsWidgetEl.innerHTML;
                countTextEl.innerText = updateCountTextEl.innerText.trim();
                if (updateActiveFacetsListEl.children.length === 0) {
                    activeFacetsWidgetEl.style.display = 'none';
                } else {
                    activeFacetsWidgetEl.style.display = 'flex';
                }

                try {
                    if (facetControlsEl) {
                        if (facetControlsEl && updateCategoryListFacetEls) {
                            reinitCategoryListFacet(facetControlsEl, updateCategoryListFacetEls, pageParam);
                        }
                        if (facetControlsEl && updateCategoryTreeFacetEls) {
                            reinitCategoryTreeFacet(facetControlsEl, updateCategoryTreeFacetEls, pageParam);
                        }
                    }

                    reinitActiveFacets(searchLayoutEl, queryInputEl, pageParam);
                    forceHardPagination(resultsWrapperEl);

                }catch (e) {
                    console.error('error preforming search', e)
                }

                httpState.requestsInFlight--;
                if (httpState.requestsInFlight === 0) {
                    spinnerEl.style.display = 'none';
                }
            };

            const delta = (Date.now() - start)
            if (delta < SPINNER_MIN_DISPLAY_DURATION) {
                setTimeout(doUpdate, SPINNER_MIN_DISPLAY_DURATION - delta);
            } else {
                doUpdate();
            }
        } else {
            // console.log("a newer request has already been completed, ignoring this one");
            httpState.requestsInFlight--;
        }
    } catch (error) {
        console.error("Something went wrong: " + error);
        httpState.requestsInFlight--;
        window.location.href = nextUrl;
    }
}


if (import.meta.hot) {
    import.meta.hot.accept();
    import.meta.hot.dispose(activeFacetGC.disposeAll);
}